import axios from 'axios'
import ElementUI from 'element-ui'
import router from '@/router'
import { clearUserInfo } from '@/utils/auth'

// 引入环境变量
const baseURL = process.env.VUE_APP_API_URL

// 防止多个请求并发返回401时重复跳转登录页
let isRedirectingToLogin = false

function redirectToLogin(message) {
  if (isRedirectingToLogin) return
  const currentPath = router.currentRoute.path
  if (currentPath === '/login') return
  isRedirectingToLogin = true
  ElementUI.Message.error(message || '登录已过期,请重新登录')
  clearUserInfo()
  router.push('/login').finally(() => {
    isRedirectingToLogin = false
  })
}

// 企业级标准axios配置
const service = axios.create({
  baseURL: baseURL,
  timeout: 15000, // 增加超时时间到15秒
  withCredentials: true, // 支持跨域携带Cookie
  headers: {
    'Content-Type': 'application/json;charset=UTF-8'
  }
})

// 请求拦截器:添加统一日志记录
service.interceptors.request.use(
  (config) => {
    // 记录请求日志(生产环境可关闭)
    if (process.env.NODE_ENV === 'development') {
      console.log(`[API请求] ${config.method?.toUpperCase()} ${config.url}`, config.params || config.data)
    }

    // 从Cookie获取token，Cookie不可用时从localStorage获取
    let token = getTokenFromCookie()
    if (!token) {
      token = localStorage.getItem('token')
    }
    if (token && config.headers) {
      config.headers['token'] = token
    }

    return config
  },
  (err) => {
    console.error('[API请求错误]', err)
    return Promise.reject(err)
  }
)

// 响应拦截器:统一处理响应格式和错误
service.interceptors.response.use(
  (response) => {
    const res = response.data

    // 检查后端Result统一格式
    if (res && typeof res === 'object') {
      // code为200表示成功
      if (res.code === 200) {
        return response // 返回完整的AxiosResponse
      } else if (res.code === 401) {
        // 未登录或登录过期(统一走防抖跳转)
        redirectToLogin(res.msg)
        return Promise.reject(new Error(res.msg || '未登录'))
      } else if (res.code === 403) {
        // 无权限访问
        ElementUI.Message.error(res.msg || '无权限访问')
        return Promise.reject(new Error(res.msg || '无权限'))
      } else {
        // 其他业务错误
        ElementUI.Message.error(res.msg || '请求失败')
        return Promise.reject(new Error(res.msg || '请求失败'))
      }
    }

    // 如果响应不是对象格式,直接返回(如文件下载等)
    return response
  },
  (error) => {
    // HTTP错误处理
    console.error('[API响应错误]', error)

    if (error.response) {
      const status = error.response.status
      switch (status) {
        case 400:
          ElementUI.Message.error('请求参数错误')
          break
        case 401:
          redirectToLogin('登录已过期,请重新登录')
          break
        case 403:
          ElementUI.Message.error('拒绝访问')
          break
        case 404:
          ElementUI.Message.error('请求地址不存在')
          break
        case 500:
          ElementUI.Message.error('服务器内部错误')
          break
        case 502:
          ElementUI.Message.error('网关错误')
          break
        case 503:
          ElementUI.Message.error('服务不可用')
          break
        case 504:
          ElementUI.Message.error('网关超时')
          break
        default:
          ElementUI.Message.error(`网络错误(${status})`)
      }
    } else if (error.code === 'ECONNABORTED') {
      ElementUI.Message.error('请求超时,请检查网络')
    } else {
      ElementUI.Message.error('网络连接失败,请检查网络')
    }

    return Promise.reject(error)
  }
)

/**
 * 从Cookie获取Token
 */
function getTokenFromCookie() {
  const cookies = document.cookie.split(';')
  for (const cookie of cookies) {
    const [name, value] = cookie.trim().split('=')
    if (name === 'token') {
      return decodeURIComponent(value)
    }
  }
  return null
}

export default service
